import React, { useEffect, useImperativeHandle, useMemo, useRef } from 'react'
import { localizedStrings } from '@core/strings'
import { NavigationAwareTabs } from '@elements'
import LinkIcon from '@mui/icons-material/Link'
import AttachIcon from '@mui/icons-material/Attachment'
import { LinkedDocumentsList } from '@elements/LinkingProcesses/LinkedDocumentsList'
import { RegisterItemDetailsTimelineTab } from '@modules/registers/items/RegisterItemDetailsTimelineTab'
import { RegisterItemDetailsProperties } from './RegisterItemDetailsProperties'
import { Box } from '@fundamentals'
import { NavigationAwareTabsRefType } from '@elements/NavigationAwareTabs/NavigationAwareTabs'
import useFeatureFlags from '@core/providers/useFeatureFlags'
import { RegisterItem } from '@core/entities/register/RegisterItem'
import { RegisterScheduleInspectionsTab } from '@modules/registers/details/registerInspections/RegisterScheduleInspectionsTab'
import type { InspectionDetailsClickParams } from '@modules/registers/details/registerInspections/RegisterScheduleInspectionsCard'
import { LinearProgress } from '@common/material'
import { RegisterItemAttachmentsTab } from '@modules/registers/items/RegisterItemAttachmentsTab'
import { useGetRegisterItemLinkedProcess } from '@core/react-query/features/registers/registers/register/items/item'

interface TabsProps {
  registerId: number
  registerItemId: number
  registerItem: RegisterItem
  organisationId: number
  isModal?: boolean
  stickyPosition: number | string
  bgColor?: string
  showPropertiesTab?: boolean
  showTimelineTab?: boolean
  tabIconSize?: 'small' | 'medium'
  tabsRef?: React.Ref<NavigationAwareTabsRefType>
  onInspectionDetailsClick?: (params: InspectionDetailsClickParams) => void
  initialTabColumnId?: string | null
}

export const RegisterItemDetailsTabs: React.FC<TabsProps> = ({
  registerId,
  registerItemId,
  registerItem,
  organisationId,
  isModal,
  stickyPosition,
  bgColor,
  showPropertiesTab = false,
  showTimelineTab = true,
  tabIconSize = 'medium',
  tabsRef,
  onInspectionDetailsClick,
  initialTabColumnId,
}) => {
  const internalTabsRef = useRef<NavigationAwareTabsRefType>(null)
  useImperativeHandle(tabsRef, () => ({
    onChangeTab: (name: string, index?: number) =>
      internalTabsRef.current?.onChangeTab(name, index),
    getActiveTab: () =>
      internalTabsRef.current?.getActiveTab() ?? { name: '', index: 0 },
  }))
  const { flags } = useFeatureFlags(['web_register_schedule_inspections'])
  const enableScheduleInspections = flags.web_register_schedule_inspections
  const { data: linkedProcess, isInitialLoading: isLoadingLinkedData } =
    useGetRegisterItemLinkedProcess({
      registerId,
      registerItemId,
    })
  const linkedPermits = linkedProcess?.getLinkedPermits() || []
  const linkedChecklists = linkedProcess?.getLinkedChecklists() || []
  const linkedInspections = linkedProcess?.getLinkedInspections() || []
  const linkedTasks =
    linkedProcess?.getLinkedTasks({ overDueFirst: true }) || []

  const scheduleInspectionConfigs = registerItem.getScheduleInspectionConfigs()
  const inspectionsCount = registerItem.getAllInspectionsCount()
  const activeInspectionsCount = registerItem.getActiveInspectionsCount()

  const attachmentProperties = registerItem.getAttachmentProperties()

  const projectId = registerItem.register?.projectId

  const tabs = useMemo(() => {
    const tabBuilder = NavigationAwareTabs.TabBuilder()
    if (showTimelineTab) {
      tabBuilder.addTab({
        name: 'timeline',
        label: localizedStrings.timeline,
        component: (
          <RegisterItemDetailsTimelineTab
            registerId={registerId}
            registerItemId={registerItemId}
            registerItem={registerItem}
          />
        ),
      })
    }

    if (showPropertiesTab) {
      tabBuilder.addTab({
        name: 'properties',
        label: localizedStrings.properties,
        component: (
          <Box sx={{ py: 2 }}>
            <RegisterItemDetailsProperties
              registerItem={registerItem}
              variant='table'
              onChangeTab={(tabName) => {
                internalTabsRef.current?.onChangeTab(tabName)
              }}
              enableScheduleInspections={!!enableScheduleInspections}
            />
          </Box>
        ),
      })
    }

    if (linkedPermits.length) {
      tabBuilder.addTab({
        name: 'permits',
        icon: <LinkIcon fontSize={tabIconSize} />,
        label: localizedStrings.permits,
        badgeCount: linkedPermits.length,
        component: (
          <LinkedDocumentsList
            items={linkedPermits}
            organisationId={organisationId}
          />
        ),
      })
    }

    if (enableScheduleInspections && inspectionsCount) {
      tabBuilder.addTab({
        name: 'inspections',
        dataTest: 'inspections',
        icon: <LinkIcon fontSize={tabIconSize} />,
        label: localizedStrings.inspections,
        badgeCount: activeInspectionsCount,
        component: (
          <RegisterScheduleInspectionsTab
            organisationId={organisationId}
            registerId={registerId}
            registerItemId={registerItemId}
            registerItem={registerItem}
            scheduleInspectionConfigs={scheduleInspectionConfigs}
            onInspectionDetailsClick={onInspectionDetailsClick}
          />
        ),
      })
    }

    if (!enableScheduleInspections && linkedInspections.length) {
      tabBuilder.addTab({
        name: 'inspections',
        icon: <LinkIcon fontSize={tabIconSize} />,
        label: localizedStrings.inspections,
        badgeCount: linkedInspections.length,
        component: (
          <LinkedDocumentsList
            items={linkedInspections}
            organisationId={organisationId}
          />
        ),
      })
    }

    if (linkedChecklists.length) {
      tabBuilder.addTab({
        name: 'checklists',
        icon: <LinkIcon fontSize={tabIconSize} />,
        label: localizedStrings.checklists,
        badgeCount: linkedChecklists.length,
        component: (
          <LinkedDocumentsList
            items={linkedChecklists}
            organisationId={organisationId}
          />
        ),
      })
    }

    if (linkedTasks.length) {
      tabBuilder.addTab({
        name: 'tasks',
        icon: <LinkIcon fontSize={tabIconSize} />,
        label: localizedStrings.tasks,
        badgeCount: linkedTasks.length,
        component: (
          <LinkedDocumentsList
            items={linkedTasks}
            organisationId={organisationId}
          />
        ),
      })
    }

    if (attachmentProperties.length) {
      tabBuilder.addTab({
        name: 'attachments',
        icon: <AttachIcon fontSize={tabIconSize} />,
        label: localizedStrings.attachments,
        component: (
          <RegisterItemAttachmentsTab
            registerId={registerId}
            registerItemId={registerItemId}
            organisationId={organisationId}
            projectId={projectId}
            readOnly={!registerItem.register?.canUpdateItems()}
          />
        ),
      })
    }

    return tabBuilder.build()
  }, [
    linkedProcess,
    organisationId,
    registerId,
    registerItemId,
    registerItem,
    enableScheduleInspections,
    showTimelineTab,
    showPropertiesTab,
    tabIconSize,
    projectId,
  ])

  const resolvedTabName = useMemo(
    () => registerItem.getInitialTabName(initialTabColumnId),
    [initialTabColumnId, registerItem],
  )

  const initialTabIndex = useMemo(() => {
    const index = tabs.findIndex((tab) => tab.name === resolvedTabName)
    return index >= 0 ? index : 0
  }, [tabs, resolvedTabName])

  useEffect(() => {
    if (!initialTabColumnId || isLoadingLinkedData || !resolvedTabName) return
    internalTabsRef.current?.onChangeTab(resolvedTabName)
  }, [initialTabColumnId, registerItemId, isLoadingLinkedData, resolvedTabName])

  return (
    <Box sx={{ position: 'relative', flex: 1, minWidth: 0 }}>
      {isLoadingLinkedData ? (
        <Box
          sx={{
            position: 'absolute',
            top: 55,
            left: 0,
            right: 0,
            zIndex: 21,
          }}
        >
          <LinearProgress />
        </Box>
      ) : (
        <NavigationAwareTabs
          ref={internalTabsRef}
          tabs={tabs}
          initialTab={initialTabIndex}
          withoutQueryParam={isModal}
          tabHeaderSx={{
            position: 'sticky',
            top: stickyPosition,
            zIndex: 20,
            bgcolor: bgColor,
          }}
        />
      )}
    </Box>
  )
}
